import { useCallback } from 'react';

import { shallowEqual } from 'react-redux';

import { Banner, Box, Button, ButtonGroup, CloseButton, Heading, Modal } from '@zillow/constellation-10';

import { useSelector } from '../../../selectors/useSelector';

import { useGetListingPreviewUrl } from '../../../hooks/queries/listingPreview';

import SpinnerContainer from '../../../components/SpinnerContainer';

import ModularListingCSEvents from '../../../model/ClickstreamEvent/ModularListingCSEvents';

const CURRENT_STEP_KEY = 'listing_preview';

type Props = {
  onClose: () => void;
  onSubmit: (arg?: Record<string, unknown>) => void;
};

const ListingPreviewModal = ({ onClose, onSubmit }: Props): JSX.Element => {
  const {
    currentProperty,
    currentProperty: { active, listingAlias },
    user,
  } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      user: state.user,
    }),
    shallowEqual,
  );

  const { data: listingPreviewData, isLoading } = useGetListingPreviewUrl(listingAlias);

  const handleSubmitClick = useCallback(() => {
    ModularListingCSEvents.Next({ currentProperty, user, currentStepKey: CURRENT_STEP_KEY });
    if (active) {
      onSubmit({ shouldTriggerDeactivate: true });
      onClose();
    } else {
      onSubmit();
    }
  }, [active]);

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={5}>Listing preview</Heading>}
      body={
        <div data-testid="ListingPreviewModal">
          <Box css={{ margin: '0 {spacing.-400}' }}>
            {isLoading ? (
              <SpinnerContainer />
            ) : (
              <Box css={{ display: 'flex', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
                <iframe
                  width={screen.width >= 1024 ? 1023 : screen.width}
                  height={screen.height}
                  src={listingPreviewData}
                  title="Listing preview of current listing"
                  style={{ border: 'none', backgroundColor: 'white' }}
                />
              </Box>
            )}
            <Banner
              closeButton={<CloseButton />}
              css={{
                position: 'absolute',
                zIndex: '200000',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                left: '50%',
              }}
            >
              This preview may not reflect recent changes and may not contain all listing details.
            </Banner>
          </Box>
        </div>
      }
      footer={
        <Box
          css={{
            display: 'flex',
            flexDirection: { base: 'column', md: 'row' },
            flex: '1 1 auto',
            justifyContent: 'end',
            alignItems: { base: 'stretch', md: 'center' },
            gap: '200',
          }}
        >
          <ButtonGroup fluid={{ base: true, md: false }} aria-label="controls">
            <Button
              data-test="back-button"
              aria-label="Cancel"
              tabIndex={0}
              tone="brand"
              emphasis="outlined"
              onClick={onClose}
              type="button"
              css={{ minWidth: '121px' }}
            >
              Back
            </Button>

            <Button
              data-testid="submit-button"
              tone="brand"
              emphasis="filled"
              onClick={handleSubmitClick}
              css={{ minWidth: '121px' }}
            >
              {active ? 'Deactivate' : 'Publish'}
            </Button>
          </ButtonGroup>
        </Box>
      }
      size={{ base: 'fullScreen' }}
    />
  );
};

export default ListingPreviewModal;
